import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, ExpressCheckoutElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { motion } from 'framer-motion';
import { ArrowLeft, Clock, Minus, Plus, Loader2, Check, MapPin, ChevronRight, Wallet, Zap, CreditCard } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import PickupTimeSelector, { isSlotBookable } from '../components/PickupTimeSelector';
import { apiFetch } from '../lib/apiClient';
import MobileNav from '../components/MobileNav';
import { Capacitor } from '@capacitor/core';
import { canUseNativePaymentSheet, payWithPaymentSheet } from '../lib/nativeStripe';

const API_URL = process.env.REACT_APP_BACKEND_URL;

async function parseJsonSafe(response) {
  if (!response) return {};
  try {
    // Read from a clone so the original response body remains untouched.
    const text = await response.clone().text();
    if (!text) return {};
    return JSON.parse(text);
  } catch {
    return {};
  }
}

const CARD_COUNTRY_ERROR_CODE = 'card_country_not_supported';

function EmbeddedPaymentForm({ orderId, onSuccess, onError, onCardRejected }) {
  const isNative = Capacitor.isNativePlatform();
  const stripe = useStripe();
  const elements = useElements();
  const [paying, setPaying] = useState(false);
  const [paymentReady, setPaymentReady] = useState(false);
  const [waitingTooLong, setWaitingTooLong] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!paymentReady) setWaitingTooLong(true);
    }, 7000);
    return () => clearTimeout(timer);
  }, [paymentReady]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    if (!paymentReady) {
      onError('Payment form is still loading. Please wait a moment.');
      return;
    }

    setPaying(true);
    try {
      const submitResult = await elements.submit();
      if (submitResult?.error) {
        throw new Error(submitResult.error.message || 'Please complete payment details');
      }

      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
      });

      if (error) {
        throw new Error(error.message || 'Payment confirmation failed');
      }

      const intentId = paymentIntent?.id;
      if (!intentId) {
        throw new Error('Missing payment intent after confirmation');
      }

      const response = await apiFetch('/api/checkout/confirm-payment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ payment_intent_id: intentId }),
      });

      const data = await parseJsonSafe(response);
      if (data.code === CARD_COUNTRY_ERROR_CODE) {
        onCardRejected(data.detail);
        return;
      }
      if (!response.ok || !data.success) {
        throw new Error(data.detail || 'Failed to finalize order');
      }

      onSuccess(orderId || data.order_id);
    } catch (err) {
      onError(err.message || 'Payment failed');
    } finally {
      setPaying(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Express checkout can render a floating wallet UI on some iOS webviews.
          Hide it so it doesn't overlap the bottom nav/profile area. */}
      {!isNative && <ExpressCheckoutElement options={{ buttonHeight: 44 }} />}
      <PaymentElement
        onReady={() => setPaymentReady(true)}
        onLoaderror={(event) => {
          const message = event?.error?.message || 'Unable to load payment form.';
          onError(message);
        }}
      />
      <Button
        type="submit"
        className="w-full rounded-xl h-12"
        disabled={!stripe || paying}
      >
        {paying ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Processing payment...
          </>
        ) : (
          'Place Order'
        )}
      </Button>
      {!paymentReady && (
        <p className="text-xs text-muted-foreground">
          Loading payment form...
        </p>
      )}
      {waitingTooLong && !paymentReady && (
        <p className="text-xs text-destructive">
          Payment form is taking longer than expected. Refresh the page and confirm keys/mode (pk_test with sk_test, or pk_live with sk_live).
        </p>
      )}
    </form>
  );
}

export default function Checkout() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, checkAuth } = useAuth();
  const { cart, updateQuantity, getTotal, clearCart } = useCart();
  
  const [selectedTime, setSelectedTime] = useState(null);
  const [selectedSkip, setSelectedSkip] = useState(null);
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [timeSlots, setTimeSlots] = useState([]);
  const [skipSlots, setSkipSlots] = useState([]);
  const [nextAvailableSlots, setNextAvailableSlots] = useState([]);
  const [now, setNow] = useState(() => Date.now());
  const [loadingSlots, setLoadingSlots] = useState(true);
  const [availableDates, setAvailableDates] = useState([]);
  const [slotsMessage, setSlotsMessage] = useState(null);
  const [pickupDate, setPickupDate] = useState(() => {
    // Default to today in America/Toronto
    try {
      return new Intl.DateTimeFormat('en-CA', {
        timeZone: 'America/Toronto',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).format(new Date());
    } catch {
      return new Date().toISOString().slice(0, 10);
    }
  });  
  // Tip state
  const [tipPercent, setTipPercent] = useState(15);
  const [customTip, setCustomTip] = useState('');
  // Default off so Apple Pay / one-off charges don't flood saved cards with duplicate PMs.
  const [saveCardForFuture, setSaveCardForFuture] = useState(false);
  
  // Order and payment state
  const [creatingOrder, setCreatingOrder] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [stripePromise, setStripePromise] = useState(null);
  const [publishableKey, setPublishableKey] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [paymentIntentId, setPaymentIntentId] = useState('');
  const [payingNative, setPayingNative] = useState(false);
  const useNativeSheet = canUseNativePaymentSheet();
  const autoPresentedSecretRef = useRef('');
  const [customerSessionClientSecret, setCustomerSessionClientSecret] = useState('');
  const [createdOrderId, setCreatedOrderId] = useState('');
  const [savedCards, setSavedCards] = useState([]);
  const [payingSavedCardId, setPayingSavedCardId] = useState('');
  const [walletBalance, setWalletBalance] = useState(null);
  const [walletAvailableBalance, setWalletAvailableBalance] = useState(null);
  const [payingWithWallet, setPayingWithWallet] = useState(false);
  const [feeConfig, setFeeConfig] = useState({
    fee_name: 'Student Builder Support Fee',
    convenience_fee_percent: 6,
    convenience_fee_min_amount: 1,
    convenience_fee_free_orders_count: 0,
    processing_percent: 2.9,
    hst_percent: 13,
  });
  const paymentSectionRef = useRef(null);

  // Check for successful payment return
  const checkPaymentStatus = useCallback(async (sessionId) => {
    setCheckingPayment(true);
    try {
      const response = await fetch(`${API_URL}/api/checkout/status/${sessionId}`, {
        credentials: 'include'
      });
      const data = await parseJsonSafe(response);
      
      if (data.payment_status === 'paid') {
        clearCart();
        toast.success('Payment successful! Redirecting to your order...');
        const orderId = data.metadata?.order_id;
        if (orderId) {
          navigate(`/orders/${orderId}`);
        } else {
          navigate('/orders');
        }
      } else if (data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
      }
    } catch (error) {
      console.error('Failed to check payment status:', error);
    } finally {
      setCheckingPayment(false);
    }
  }, [clearCart, navigate]);

  // Check for payment return or cancelled payment
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
    if (searchParams.get('cancelled') === 'true') {
      toast.error('Payment was cancelled');
    }
  }, [searchParams, checkPaymentStatus]);

  useEffect(() => {
    const loadStripeConfig = async () => {
      try {
        const response = await fetch(`${API_URL}/api/config/stripe`);
        const data = await parseJsonSafe(response);
        if (response.ok && data.publishable_key) {
          setPublishableKey(data.publishable_key);
          setStripePromise(loadStripe(data.publishable_key));
        }
      } catch (error) {
        console.error('Failed to load Stripe config:', error);
      }
    };
    loadStripeConfig();
  }, []);

  useEffect(() => {
    const loadSavedCards = async () => {
      try {
        const response = await apiFetch('/api/checkout/saved-payment-methods');
        const data = await parseJsonSafe(response);
        if (response.ok) {
          setSavedCards(data.payment_methods || []);
        }
      } catch (error) {
        console.error('Failed to load saved cards:', error);
      }
    };
    loadSavedCards();
  }, []);

  useEffect(() => {
    const loadWalletBalance = async () => {
      if (!user?.phone_verified) {
        setWalletBalance(null);
        return;
      }
      try {
        const response = await apiFetch('/api/wallet/balance');
        const data = await parseJsonSafe(response);
        if (response.ok) {
          setWalletBalance(data.wallet_balance ?? 0);
          setWalletAvailableBalance(data.wallet_available_balance ?? data.wallet_balance ?? 0);
        }
      } catch (error) {
        console.error('Failed to load wallet balance:', error);
      }
    };
    loadWalletBalance();
  }, [user?.phone_verified, createdOrderId]);

  useEffect(() => {
    const loadFeeConfig = async () => {
      try {
        const response = await apiFetch('/api/fee-config');
        const data = await parseJsonSafe(response);
        if (response.ok && data) setFeeConfig(data);
      } catch (error) {
        console.error('Failed to load fee config:', error);
      }
    };
    loadFeeConfig();
  }, []);

  // Fetch time slots for selected day
  useEffect(() => {
    const fetchTimeSlots = async () => {
      if (!cart.truckId || !pickupDate) return;
      setLoadingSlots(true);
      try {
        const response = await fetch(
          `${API_URL}/api/trucks/${cart.truckId}/time-slots?date=${encodeURIComponent(pickupDate)}`
        );
        const data = await parseJsonSafe(response);
        setTimeSlots(data.slots || []);
        setSkipSlots(data.skip_slots || []);
        setNextAvailableSlots(data.next_available_slots || []);
        setAvailableDates(data.available_dates || []);
        setSlotsMessage(data.message || null);
        // If selected date is closed / missing from week list, prefer first bookable day
        if (Array.isArray(data.available_dates) && data.available_dates.length > 0) {
          const stillValid = data.available_dates.some(
            (d) => d.date === pickupDate && !d.is_closed
          );
          if (!stillValid) {
            const firstOpen = data.available_dates.find((d) => !d.is_closed);
            if (firstOpen) setPickupDate(firstOpen.date);
          }
        }
      } catch (error) {
        console.error('Failed to fetch time slots:', error);
      } finally {
        setLoadingSlots(false);
      }
    };
    fetchTimeSlots();
  }, [cart.truckId, pickupDate]);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!selectedTime || selectedSkip || clientSecret) return;
    if (!isSlotBookable({ time: selectedTime }, now)) {
      setSelectedTime(null);
      toast.info('That pickup time is now less than 15 minutes away. Please pick another.');
    }
  }, [now, selectedTime, selectedSkip, clientSecret]);

  useEffect(() => {
    if (selectedSkip && !skipSlots.some((s) => s.minutes === selectedSkip.minutes)) {
      setSelectedSkip(null);
      setSelectedTime(null);
    }
  }, [skipSlots, selectedSkip]);

  const handleSelectPickupDate = (date) => {
    if (date === pickupDate) return;
    setPickupDate(date);
    setSelectedTime(null);
    setSelectedSkip(null);
  };
  // Calculate tip
  const subtotal = getTotal();
  const parsedCustomTip = Number.parseFloat(customTip);
  const tipAmount = Number.isFinite(parsedCustomTip) && parsedCustomTip >= 0
    ? parsedCustomTip
    : (subtotal * tipPercent / 100);
  const roundCents = (value) => Math.round((Number(value) || 0) * 100) / 100;
  const processingPercent = Number(feeConfig.processing_percent ?? 2.9) || 0;
  const hst = roundCents(subtotal * 0.13);
  const processing = roundCents((subtotal + hst) * processingPercent / 100);
  const tax = roundCents(hst + processing);
  const foodTotal = subtotal + tax + tipAmount;
  const freeOrderCutoff = Number(feeConfig.convenience_fee_free_orders_count || 0);
  const isFeeFreeOrder =
    freeOrderCutoff > 0 && Number(user?.perk_orders_used ?? user?.total_orders ?? 0) < freeOrderCutoff;
  const regularPlatformFee = Math.max(
    Number(feeConfig.convenience_fee_min_amount || 0),
    (subtotal + tax) * (Number(feeConfig.convenience_fee_percent || 0) / 100)
  );
  const platformFee = isFeeFreeOrder ? 0 : regularPlatformFee;
  const skipFee = selectedSkip ? Number(selectedSkip.fee || 0) : 0;
  const total = foodTotal + platformFee + skipFee;
  const discountEligible = false;
  const discountPercent = 0;
  const discountAmount = 0;
  const walletTotal = total;

  // Create order and initialize embedded Stripe payment
  const handleProceedToPayment = async () => {
    if (!selectedTime) {
      toast.error('Please select a pickup time');
      return;
    }

    if (cart.items.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    setCreatingOrder(true);

    try {
      // Create order first
      const orderResponse = await apiFetch('/api/orders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          truck_id: cart.truckId,
          items: cart.items.map(item => ({
            item_id: item.item_id,
            quantity: item.quantity,
            modifiers: item.modifiers || []
          })),
          scheduled_pickup_time: selectedTime,
          skip_the_line_minutes: selectedSkip ? selectedSkip.minutes : null,
          special_instructions: specialInstructions || null,
          tip_amount: tipAmount
        })
      });

      if (!orderResponse.ok) {
        const error = await parseJsonSafe(orderResponse);
        if (orderResponse.status === 401) {
          throw new Error('Session expired. Please sign in again.');
        }
        throw new Error(error.detail || 'Failed to create order');
      }

      const order = await parseJsonSafe(orderResponse);
      
      // Create payment intent for embedded checkout
      const checkoutResponse = await apiFetch('/api/checkout/create-payment-intent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          order_id: order.order_id,
          tip_amount: tipAmount,
          save_payment_method: saveCardForFuture
        })
      });

      if (!checkoutResponse.ok) {
        const error = await parseJsonSafe(checkoutResponse);
        if (checkoutResponse.status === 401) {
          throw new Error('Session expired. Please sign in again.');
        }
        throw new Error(error.detail || 'Failed to create checkout session');
      }

      const data = await parseJsonSafe(checkoutResponse);
      setCreatedOrderId(order.order_id);
      setClientSecret(data.client_secret || '');
      setPaymentIntentId(
        data.payment_intent_id || (data.client_secret || '').split('_secret_')[0] || ''
      );
      setCustomerSessionClientSecret(data.customer_session_client_secret || '');
      setTimeout(() => {
        paymentSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);

    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.message || 'Checkout failed');
      setCreatingOrder(false);
    }
  };

  const handleEmbeddedPaymentSuccess = useCallback((orderId) => {
    clearCart();
    toast.success('Payment authorized — charged only if the vendor accepts.');
    navigate(`/orders/${orderId}`);
  }, [clearCart, navigate]);

  // The rejected PaymentIntent was cancelled server-side; start a fresh one on the same order to retry.
  const handleCardCountryRejected = useCallback(async (message) => {
    toast.error(message || 'Only Canadian-issued cards are accepted.', { duration: 10000 });
    if (!createdOrderId) return;
    try {
      const response = await apiFetch('/api/checkout/create-payment-intent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          order_id: createdOrderId,
          tip_amount: tipAmount,
          save_payment_method: saveCardForFuture,
        }),
      });
      const data = await parseJsonSafe(response);
      if (!response.ok) throw new Error(data.detail || 'Could not restart payment');
      const nextSecret = data.client_secret || '';
      // Don't auto-reopen the native sheet right after the rejection message.
      autoPresentedSecretRef.current = nextSecret;
      setClientSecret(nextSecret);
      setPaymentIntentId(data.payment_intent_id || nextSecret.split('_secret_')[0] || '');
      setCustomerSessionClientSecret(data.customer_session_client_secret || '');
    } catch (error) {
      toast.error(error.message || 'Could not restart payment. Please go back and try again.');
    }
  }, [createdOrderId, tipAmount, saveCardForFuture]);

  const handleNativePayment = useCallback(async () => {
    if (!clientSecret || !createdOrderId) {
      toast.error('Please create order first');
      return;
    }
    setPayingNative(true);
    try {
      const result = await payWithPaymentSheet({ publishableKey, clientSecret });
      if (result === 'canceled') {
        toast('Payment cancelled');
        return;
      }
      if (result !== 'completed') {
        throw new Error('Payment failed. Please try again.');
      }

      const response = await apiFetch('/api/checkout/confirm-payment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ payment_intent_id: paymentIntentId }),
      });
      const data = await parseJsonSafe(response);
      if (data.code === CARD_COUNTRY_ERROR_CODE) {
        await handleCardCountryRejected(data.detail);
        return;
      }
      if (!response.ok || !data.success) {
        throw new Error(data.detail || 'Failed to finalize order');
      }
      handleEmbeddedPaymentSuccess(createdOrderId || data.order_id);
    } catch (error) {
      toast.error(error?.message || 'Payment failed');
    } finally {
      setPayingNative(false);
    }
  }, [clientSecret, createdOrderId, publishableKey, paymentIntentId, handleEmbeddedPaymentSuccess, handleCardCountryRejected]);

  useEffect(() => {
    if (!useNativeSheet || !clientSecret || !publishableKey) return;
    if (autoPresentedSecretRef.current === clientSecret) return;
    autoPresentedSecretRef.current = clientSecret;
    handleNativePayment();
  }, [useNativeSheet, clientSecret, publishableKey, handleNativePayment]);

  const canPayWithWallet =
    user?.phone_verified
    && walletAvailableBalance != null
    && walletAvailableBalance >= walletTotal - 0.001;

  const handlePayWithWallet = async () => {
    if (!createdOrderId) {
      toast.error('Please create order first');
      return;
    }
    setPayingWithWallet(true);
    try {
      const response = await apiFetch('/api/checkout/pay-with-wallet', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order_id: createdOrderId }),
      });
      const data = await parseJsonSafe(response);
      if (!response.ok || !data.success) {
        throw new Error(data.detail || 'Wallet payment failed');
      }
      await checkAuth();
      setWalletBalance(data.wallet_balance ?? 0);
      setWalletAvailableBalance(data.wallet_available_balance ?? data.wallet_balance ?? 0);
      handleEmbeddedPaymentSuccess(createdOrderId);
      toast.success('Wallet funds held — charged only if the vendor accepts.');
    } catch (error) {
      toast.error(error.message || 'Wallet payment failed');
    } finally {
      setPayingWithWallet(false);
    }
  };

  const handlePayWithSavedCard = async (paymentMethodId) => {
    if (!createdOrderId) {
      toast.error('Please create order first');
      return;
    }
    setPayingSavedCardId(paymentMethodId);
    try {
      const response = await apiFetch('/api/checkout/pay-with-saved-method', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          order_id: createdOrderId,
          payment_method_id: paymentMethodId,
        }),
      });
      const data = await parseJsonSafe(response);
      if (data.code === CARD_COUNTRY_ERROR_CODE) {
        toast.error(data.detail, { duration: 10000 });
        return;
      }
      if (!response.ok || !data.success) {
        throw new Error(data.detail || 'Saved-card payment failed');
      }
      handleEmbeddedPaymentSuccess(data.order_id);
    } catch (error) {
      toast.error(error.message || 'Saved-card payment failed');
    } finally {
      setPayingSavedCardId('');
    }
  };

  if (cart.items.length === 0) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div
          className="max-w-md mx-auto px-4 text-center"
          style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 6rem)' }}
        >
          <h1 className="text-2xl font-bold mb-4">Your cart is empty</h1>
          <p className="text-muted-foreground mb-6">Add some delicious items from a food truck!</p>
          <Button onClick={() => navigate('/')} data-testid="browse-trucks-btn">
            Browse Food Trucks
          </Button>
        </div>
        <MobileNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-safe">
      <Header />
      
      <div
        className="max-w-lg mx-auto px-4"
        style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 5.5rem)' }}
      >
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-2xl font-bold">Checkout</h1>
            <div className="flex items-center gap-2 text-muted-foreground mt-1">
              <MapPin className="w-4 h-4" />
              <span>{cart.truckName}</span>
            </div>
          </div>

          {/* Order Items */}
          <div className="bg-card rounded-2xl p-4 mb-4 shadow-sm border">
            <h2 className="font-semibold mb-4">Your Order</h2>
            <div className="space-y-3">
              {cart.items.map(item => (
                <div key={item.item_id} className="flex items-center gap-3" data-testid={`cart-item-${item.item_id}`}>
                  {item.photo_url && (
                    <img 
                      src={item.photo_url} 
                      alt={item.name}
                      className="w-14 h-14 rounded-xl object-cover"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-sm">{item.name}</h3>
                    <p className="text-sm text-muted-foreground">
                      CA$ {item.price.toFixed(2)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button 
                      className="w-8 h-8 rounded-full bg-muted flex items-center justify-center hover:bg-muted/80"
                      onClick={() => updateQuantity(item.item_id, item.quantity - 1)}
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <span className="w-6 text-center font-medium">{item.quantity}</span>
                    <button 
                      className="w-8 h-8 rounded-full bg-muted flex items-center justify-center hover:bg-muted/80"
                      onClick={() => updateQuantity(item.item_id, item.quantity + 1)}
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-card rounded-2xl p-4 mb-4 shadow-sm border">
            <div className="flex items-center gap-2 mb-4">
              <Clock className="w-5 h-5 text-primary" />
              <h2 className="font-semibold">Pickup Time</h2>
            </div>
            <PickupTimeSelector
              dates={availableDates}
              selectedDate={pickupDate}
              onSelectDate={handleSelectPickupDate}
              slots={timeSlots}
              skipSlots={skipSlots}
              nextAvailableSlots={nextAvailableSlots}
              now={now}
              loading={loadingSlots}
              selectedTime={selectedTime}
              selectedSkipMinutes={selectedSkip ? selectedSkip.minutes : null}
              message={slotsMessage}
              onSelect={(time) => {
                setSelectedSkip(null);
                setSelectedTime(time);
              }}
              onSelectSkip={(slot) => {
                setSelectedSkip(slot);
                setSelectedTime(slot.time);
              }}
            />

            {/* Summary line so it appears near the pickup selector */}
            {selectedTime && (
              <div className="mt-4 p-4 bg-green-50 rounded-xl flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                  <Check className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="font-medium text-green-800">
                    {selectedSkip ? 'Skip the line' : 'Pickup scheduled'}
                  </p>
                  <p className="text-sm text-green-700">
                    {selectedSkip
                      ? `In ${selectedSkip.minutes} min`
                      : new Date(selectedTime).toLocaleString('en-US', {
                          weekday: 'short',
                          month: 'short',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                          hour12: true,
                          timeZone: 'America/Toronto',
                        })}{' '}
                    at {cart.truckName}
                  </p>
                </div>
              </div>
            )}
          </div>

          <div className="bg-card rounded-2xl p-4 mb-4 shadow-sm border">
            <h2 className="font-semibold mb-2">Special Instructions</h2>
            <textarea
              className="w-full p-3 rounded-xl bg-muted border-0 resize-none text-base"
              placeholder="Any allergies or special requests?"
              rows={2}
              value={specialInstructions}
              onChange={(e) => setSpecialInstructions(e.target.value)}
              data-testid="special-instructions"
            />
          </div>

          <div className="bg-card rounded-2xl p-4 mb-4 shadow-sm border">
            <h2 className="font-semibold mb-3">Add a tip</h2>
            <div className="grid grid-cols-3 gap-2 mb-3">
              {[5, 10, 15].map(percent => (
                <button
                  key={percent}
                  onClick={() => { setTipPercent(percent); setCustomTip(''); }}
                  className={`py-3 rounded-xl text-sm font-medium transition-all ${
                    tipPercent === percent && !customTip
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted hover:bg-muted/80'
                  }`}
                >
                  {percent}%
                </button>
              ))}
            </div>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-base text-muted-foreground pointer-events-none">
                CA$
              </span>
              <input
                type="number"
                inputMode="decimal"
                placeholder="Custom amount"
                value={customTip}
                onChange={(e) => setCustomTip(e.target.value)}
                className="w-full pl-14 pr-4 py-3 rounded-xl bg-muted border-0 text-base"
              />
            </div>
          </div>

          <div className="bg-card rounded-2xl p-4 mb-4 shadow-sm border">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={saveCardForFuture}
                onChange={(e) => setSaveCardForFuture(e.target.checked)}
                className="mt-1"
              />
              <div>
                <p className="font-medium">Save card for faster checkout</p>
                <p className="text-xs text-muted-foreground">
                  Your card is securely stored by Stripe and can be reused next time.
                </p>
              </div>
            </label>
            {savedCards.length > 0 && (
              <p className="text-xs text-muted-foreground mt-3">
                Saved cards available: {savedCards.map((c) => `${c.brand.toUpperCase()} •••• ${c.last4}`).join(', ')}
              </p>
            )}
          </div>

          {/* Order Summary */}
          <div className="bg-card rounded-2xl p-4 mb-4 shadow-sm border">
            <h2 className="font-semibold mb-3">Order Summary</h2>

            {discountEligible && discountPercent > 0 && (
              <div className="mb-3 p-3 rounded-xl bg-primary/5 border border-primary/20">
                <div className="flex items-center justify-between gap-2">
                  <span className="inline-flex items-center rounded-full bg-primary/10 text-primary text-xs font-semibold px-2.5 py-1">
                    {discountPercent}% off applied — wallet payment only
                  </span>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    −CA$ {discountAmount.toFixed(2)}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Order {discountOrderNumber} of 15 discounted orders — {discountPercent}% off today
                </p>
              </div>
            )}

            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Subtotal</span>
                <span>CA$ {subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Taxes plus non-gov fee</span>
                <span>CA$ {tax.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Tip</span>
                <span>CA$ {tipAmount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">{feeConfig.fee_name || 'Student Builder Support Fee'}</span>
                {isFeeFreeOrder && regularPlatformFee > 0 ? (
                  <span className="inline-flex items-center gap-2">
                    <span className="text-muted-foreground line-through">CA$ {regularPlatformFee.toFixed(2)}</span>
                    <span>CA$ 0.00</span>
                  </span>
                ) : (
                  <span>CA$ {platformFee.toFixed(2)}</span>
                )}
              </div>
              {isFeeFreeOrder && (
                <p className="text-xs text-right text-primary">
                  Fee waived for your first {freeOrderCutoff} orders
                </p>
              )}
              {skipFee > 0 && (
                <div className="flex justify-between" data-testid="skip-the-line-fee">
                  <span className="inline-flex items-center gap-1 text-primary">
                    <Zap className="w-3.5 h-3.5 fill-current" />
                    Skip the line
                  </span>
                  <span>CA$ {skipFee.toFixed(2)}</span>
                </div>
              )}
              <div className="border-t pt-2 mt-2 flex justify-between font-semibold text-lg">
                <span>Total</span>
                <span className="text-primary">CA$ {total.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {!clientSecret ? (
            <div
              className="fixed left-0 right-0 p-4 border-t bg-background/95 backdrop-blur md:static md:p-0 md:border-0 md:bg-transparent"
              style={{ bottom: '4rem' }}
            >
              <Button 
                size="lg" 
                className="w-full rounded-xl h-14 text-base font-semibold max-w-lg mx-auto"
                onClick={handleProceedToPayment}
                disabled={creatingOrder || !selectedTime}
                data-testid="continue-to-payment-btn"
              >
                {creatingOrder ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Creating Order...
                  </>
                ) : (
                  <>
                    Continue to Payment
                    <ChevronRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
            </div>
          ) : (
            <div ref={paymentSectionRef} className="bg-card rounded-2xl p-4 shadow-sm border">
              <h2 className="font-semibold mb-1">Payment</h2>
              <p className="text-xs text-muted-foreground mb-2">
                Your payment is authorized now and charged only if the vendor accepts your order.
              </p>
              <div
                className="flex items-start gap-2 rounded-xl bg-muted px-3 py-2 mb-3 text-xs text-muted-foreground"
                data-testid="canadian-cards-note"
              >
                <CreditCard className="w-4 h-4 mt-0.5 shrink-0" />
                <span>Only Canadian-issued cards are accepted (including Apple Pay cards).</span>
              </div>
              {canPayWithWallet && (
                <div className="mb-4">
                  <Button
                    className="w-full rounded-xl h-12 bg-primary"
                    disabled={payingWithWallet}
                    onClick={handlePayWithWallet}
                    data-testid="pay-with-wallet-btn"
                  >
                    {payingWithWallet ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Processing…
                      </>
                    ) : (
                      <>
                        <Wallet className="w-4 h-4 mr-2" />
                        Pay with Wallet (CA$ {walletTotal.toFixed(2)})
                      </>
                    )}
                  </Button>
                  <p className="text-xs text-center text-muted-foreground mt-2">
                    Funds are held on your wallet until the vendor accepts
                  </p>
                </div>
              )}
              {user?.phone_verified && walletAvailableBalance != null && walletAvailableBalance < walletTotal && (
                <p className="text-xs text-muted-foreground mb-3">
                  Available wallet balance CA$ {walletAvailableBalance.toFixed(2)}
                  {(walletBalance ?? 0) > walletAvailableBalance && (
                    <> ({((walletBalance ?? 0) - walletAvailableBalance).toFixed(2)} held for pending orders)</>
                  )}
                  {' — '}
                  <Link to="/wallet" className="text-primary underline">
                    add funds
                  </Link>{' '}
                  or pay by card below.
                </p>
              )}
              {canPayWithWallet && savedCards.length > 0 && (
                <p className="text-sm font-medium text-muted-foreground mb-2 text-center">or pay by card</p>
              )}
              {savedCards.length > 0 && (
                <div className="mb-4 space-y-2">
                  <p className="text-sm font-medium">Pay with saved card</p>
                  {savedCards.map((c) => {
                    const accepted = c.accepted !== false;
                    return (
                      <Button
                        key={c.id}
                        variant="outline"
                        className="w-full justify-between"
                        disabled={!!payingSavedCardId || !accepted}
                        onClick={() => handlePayWithSavedCard(c.id)}
                      >
                        <span>{c.brand.toUpperCase()} •••• {c.last4} (exp {c.exp_month}/{c.exp_year})</span>
                        {!accepted
                          ? 'Non-Canadian card'
                          : payingSavedCardId === c.id ? 'Processing...' : 'Pay'}
                      </Button>
                    );
                  })}
                </div>
              )}
              {useNativeSheet ? (
                <Button
                  className="w-full rounded-xl h-12 bg-black text-white hover:bg-black/90"
                  disabled={payingNative || !publishableKey}
                  onClick={handleNativePayment}
                  data-testid="native-pay-btn"
                >
                  {payingNative ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing payment...
                    </>
                  ) : !publishableKey ? (
                    'Loading payment options...'
                  ) : (
                    'Pay with Apple Pay or card'
                  )}
                </Button>
              ) : stripePromise ? (
                <Elements
                  key={`${clientSecret}:${customerSessionClientSecret || 'no-cs'}`}
                  stripe={stripePromise}
                  options={{
                    clientSecret,
                    customerSessionClientSecret: customerSessionClientSecret || undefined,
                    appearance: { variables: { fontSizeBase: '16px' } },
                  }}
                >
                  <EmbeddedPaymentForm
                    orderId={createdOrderId}
                    onSuccess={handleEmbeddedPaymentSuccess}
                    onError={(message) => toast.error(message)}
                    onCardRejected={handleCardCountryRejected}
                  />
                </Elements>
              ) : (
                <div className="text-sm text-muted-foreground">Loading payment options...</div>
              )}
            </div>
          )}

          {/* (Pickup scheduled summary moved into Pickup Time card) */}
        </motion.div>
      </div>

      <MobileNav />
    </div>
  );
}
